import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { envService } from './envService';
import { logAuthDiagnostic } from '../diagnostics/authDiagnostic';
import { showError } from '../toastservice/toastService';
import { store } from '../../store/store';
import { setLoading } from '../../store/slices/appSlice';
import { setShortToken } from '../../store/slices/authSlice';

/**
 * Global axios interceptor that handles two classes of transient failures
 * in the visitor prefill flow:
 *
 * 1. 401 "SecurityContext not found" — the JWT vanished from sessionStorage
 *    mid-flow (iOS Safari WKWebView eviction, stale PWA shell, etc). The
 *    interceptor re-mints a fresh JWT from the prefill short token and
 *    replays the original request once.
 *
 * 2. Network Errors (no response object) — a cellular hiccup, Cloudflare/
 *    CloudFront transient drop, or TCP RST mid-response. Backend usually
 *    processed the request but the response bytes never reached the client.
 *    The interceptor silently retries the request once for endpoints that
 *    are safe to retry.
 *
 * Background: visitors-web is a one-shot prefill flow. When the user clicks
 * the "Prefill Details" link in their email they land on `/prefill?token=XXX`.
 * `PreRegister.getToken()` exchanges that short token for a 1-hour JWT and
 * stores it in `sessionStorage.accessToken`. Subsequent screens reuse the JWT
 * via `touchlessService.getAuthHeaders()`.
 *
 * Both recovery paths are strictly capped at one attempt per request so no
 * real failure can loop.
 */

const SHORT_TOKEN_KEY = 'prefillShortToken';
const AUTH_RETRY_FLAG = '__visitlyAuthRetried';
const NET_RETRY_FLAG = '__visitlyNetRetried';
const NET_RETRY_DELAY_MS = 500;

/** Persist the short token in Redux store (primary) + sessionStorage (fallback). */
export const savePrefillShortToken = (shortToken: string): void => {
  if (shortToken) {
    store.dispatch(setShortToken(shortToken)); // also writes sessionStorage as side-effect
  }
};

/** Read the short token — Redux store is primary, sessionStorage is fallback, URL is last resort. */
const getShortToken = (): string | null => {
  const fromStore = store.getState().auth?.shortToken;
  if (fromStore) return fromStore;
  const fromSession = sessionStorage.getItem(SHORT_TOKEN_KEY);
  if (fromSession) return fromSession;
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get('token');
  } catch {
    return null;
  }
};

type RetriableConfig = InternalAxiosRequestConfig & {
  [AUTH_RETRY_FLAG]?: boolean;
  [NET_RETRY_FLAG]?: boolean;
};

/**
 * Re-mint the JWT by calling /v1/visitor/authenticate?tkn=<shortToken>.
 * Uses a bare axios call (no interceptors) to avoid recursion.
 */
const reAuthenticate = async (shortToken: string): Promise<string | null> => {
  try {
    const response = await axios.post(
      `${envService.apiUrl}/visitor/authenticate?tkn=${encodeURIComponent(shortToken)}`,
      {},
      { headers: {} }, // explicit empty headers — endpoint is whitelisted
    );
    const accessToken = response?.data?.accessToken;
    if (typeof accessToken === 'string' && accessToken.length > 0) {
      const bearer = `Bearer ${accessToken}`;
      // Keep Redux store and sessionStorage in sync
      store.dispatch({ type: 'auth/setTokens', payload: { accessToken: bearer } });
      return bearer;
    }
    return null;
  } catch (err) {
    console.error('Visitly: silent re-authentication failed', err);
    return null;
  }
};

/**
 * A failure is a "Network Error" if axios never received a response.
 * Common causes: cellular drop, TCP RST, CORS preflight failure, DNS blip,
 * client-side timeout before any bytes arrived.
 */
const isNetworkError = (error: AxiosError): boolean => {
  if (error?.response) return false;
  if (error?.code === 'ERR_NETWORK') return true;
  if (error?.code === 'ECONNABORTED') return true;
  // Axios pre-v1 and some adapters set only `message`
  return error?.message === 'Network Error';
};

/**
 * Is this request safe to blindly retry?
 * - Any idempotent HTTP method (GET, HEAD, OPTIONS)
 * - POST /v1/visitor/authenticate — explicitly audited as read-only
 *   (looks up Redis, mints JWT, no state change)
 *
 * Anything else (POST /visits, POST /visits/checkDocExpiry, POST /visits/signout,
 * PATCH /visits/**, etc.) might create or mutate state — we must NOT retry.
 */
const isSafeToRetry = (config: RetriableConfig): boolean => {
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' || method === 'head' || method === 'options') {
    return true;
  }
  const url = (config.url || '') + '';
  if (method === 'post' && url.includes('/visitor/authenticate')) {
    return true;
  }
  return false;
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Install the interceptor onto the global axios instance.
 * Must be called once at app startup, before any API calls run.
 *
 * Returns the interceptor id so tests (or hot-reload) can eject it.
 */
export const installAxiosAuthInterceptor = (): number => {
  return axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error?.config as RetriableConfig | undefined;
      if (!config) {
        return Promise.reject(error);
      }

      // --- Network Error recovery path -------------------------------------
      if (isNetworkError(error)) {
        if (config[NET_RETRY_FLAG] || !isSafeToRetry(config)) {
          logAuthDiagnostic({
            reason: 'request_network_error',
            apiUrl: config.url,
            apiMethod: config.method,
            errorCode: error.code,
            errorMessage: error.message,
            recovered: false,
            extra: { retried: !!config[NET_RETRY_FLAG], safeToRetry: isSafeToRetry(config) },
          });
          showError('A network error occurred. Please check your connection and try again.');
          store.dispatch(setLoading(false));
          return Promise.reject(error);
        }
        config[NET_RETRY_FLAG] = true;
        await sleep(NET_RETRY_DELAY_MS);
        return axios.request(config);
      }

      // --- 401 re-authentication path --------------------------------------
      const status = error?.response?.status;

      // 403 — usually real short-token expiry from /visitor/authenticate.
      // Log diagnostic once so we can see what state the visitor is in when
      // the backend rejects their short token.
      if (status === 403) {
        logAuthDiagnostic({
          reason: 'request_403',
          apiUrl: config.url,
          apiMethod: config.method,
          errorStatus: status,
          errorMessage: error.message,
        });
        return Promise.reject(error);
      }

      if (status !== 401 || config[AUTH_RETRY_FLAG]) {
        return Promise.reject(error);
      }

      // Don't try to recover the auth call itself.
      const requestUrl = (config.url || '') + '';
      if (requestUrl.includes('/visitor/authenticate')) {
        logAuthDiagnostic({
          reason: 'request_401',
          apiUrl: config.url,
          apiMethod: config.method,
          errorStatus: status,
          errorMessage: error.message,
          recovered: false,
          extra: { skipped_recursion_guard: true },
        });
        return Promise.reject(error);
      }

      const shortToken = getShortToken();
      if (!shortToken) {
        logAuthDiagnostic({
          reason: 'interceptor_no_short_token',
          apiUrl: config.url,
          apiMethod: config.method,
          errorStatus: status,
          errorMessage: error.message,
          recovered: false,
        });
        showError(
          "Oops! Your session has timed out. Just head back to your email and click the Pre-fill or Register button to continue.",
        );
        store.dispatch(setLoading(false));
        return Promise.reject(error);
      }

      // Snapshot BEFORE recovery attempt so we know what state the visitor
      // was in when the 401 hit.
      logAuthDiagnostic({
        reason: 'request_401',
        apiUrl: config.url,
        apiMethod: config.method,
        errorStatus: status,
        errorMessage: error.message,
        recovered: false,
        extra: { willAttemptRecovery: true },
      });

      const newBearer = await reAuthenticate(shortToken);
      if (!newBearer) {
        logAuthDiagnostic({
          reason: 'interceptor_reauth_failed',
          apiUrl: config.url,
          apiMethod: config.method,
          errorStatus: status,
          recovered: false,
        });
        showError(
          'Your session has expired. Please re-open the link from your email to continue.',
        );
        store.dispatch(setLoading(false));
        return Promise.reject(error);
      }

      // Retry the original request once with the fresh token.
      config[AUTH_RETRY_FLAG] = true;
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = newBearer;

      logAuthDiagnostic({
        reason: 'interceptor_recovered',
         type: 'info',
        apiUrl: config.url,
        apiMethod: config.method,
        errorStatus: status,
        recovered: true,
      });

      return axios.request(config);
    },
  );
};
